import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { ROLE_LABELS } from '../../utils/constants';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, Save, Lock, RotateCcw } from 'lucide-react';

/* Roles & Permissions matrix — super_admin/admin (any org) + org_admin (own org).
   Granular module-based permissions: each module (Batches, Trainees, Attendance…)
   exposes individual view/create/edit/delete actions per configurable role. */
export default function RolesPermissionsTab() {
  const { get, put } = useApi();
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [matrix, setMatrix] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cat = await get('/roles/catalog');
      const m = await get('/roles/permissions');
      setModules(cat.modules || []);
      setRoles(cat.configurable_roles || []);
      setDefaults(cat.defaults || {});
      setMatrix(m.matrix || {});
    } catch {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const has = (role, key) => (matrix[role] || []).includes(key);

  const setRolePerms = (role, updater) => {
    setMatrix((prev) => {
      const cur = new Set(prev[role] || []);
      updater(cur);
      return { ...prev, [role]: Array.from(cur) };
    });
    setDirty((prev) => ({ ...prev, [role]: true }));
  };

  const toggle = (role, key) =>
    setRolePerms(role, (cur) => { if (cur.has(key)) cur.delete(key); else cur.add(key); });

  const toggleModule = (role, mod) => {
    const keys = mod.actions.map((a) => a.key);
    const allOn = keys.every((k) => has(role, k));
    setRolePerms(role, (cur) => keys.forEach((k) => (allOn ? cur.delete(k) : cur.add(k))));
  };

  const resetToDefaults = (role) => {
    setMatrix((prev) => ({ ...prev, [role]: [...(defaults[role] || [])] }));
    setDirty((prev) => ({ ...prev, [role]: true }));
  };

  const save = async (role) => {
    setSaving(role);
    try {
      await put(`/roles/${role}/permissions`, { permissions: matrix[role] || [] });
      setDirty((prev) => ({ ...prev, [role]: false }));
      toast.success(`${ROLE_LABELS[role] || role} permissions saved`);
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-8" data-testid="roles-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading permissions…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="roles-permissions-tab">
      <div className="space-y-1.5">
        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-settings)]/10 text-[var(--lumyn-settings)] text-[11px] font-semibold uppercase tracking-[0.14em]">
          <ShieldCheck className="w-3.5 h-3.5" /> Roles & Permissions
        </div>
        <h2 className="font-display text-xl font-bold tracking-tight">Permission matrix</h2>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Each module exposes granular actions. Grant exactly what each role needs —
          changes apply immediately across the app.
        </p>
      </div>

      <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="roles-bypass-note">
        <Lock className="w-4 h-4 mt-0.5 shrink-0" />
        <span><strong>Super Admin</strong> and <strong>Admin</strong> always have full access and cannot be edited here. Org Admins manage this matrix for their own organization; no role can ever be granted matrix-management itself (anti-escalation).</span>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left font-semibold p-3 min-w-[260px] sticky left-0 bg-muted/40 backdrop-blur z-10">Permission</th>
              {roles.map((role) => (
                <th key={role} className="text-center font-semibold p-3 min-w-[110px] align-bottom">
                  <div className="font-display text-[13px]">{ROLE_LABELS[role] || role}</div>
                  <div className="flex flex-col items-center gap-1 mt-2">
                    <Button
                      size="sm"
                      variant={dirty[role] ? 'default' : 'outline'}
                      className="h-7 px-2 text-xs"
                      disabled={!dirty[role] || saving === role}
                      onClick={() => save(role)}
                      data-testid={`save-role-${role}-btn`}
                    >
                      {saving === role
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <><Save className="w-3 h-3 mr-1" />Save</>}
                    </Button>
                    <button
                      type="button"
                      className="text-[10px] font-normal text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
                      onClick={() => resetToDefaults(role)}
                      data-testid={`reset-role-${role}-btn`}
                    >
                      <RotateCcw className="w-2.5 h-2.5" /> defaults
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modules.map((mod) => (
              [
                <tr key={mod.key} className="border-b bg-muted/25" data-testid={`module-row-${mod.key}`}>
                  <td className="p-2.5 pl-3 sticky left-0 bg-muted/25 backdrop-blur z-10">
                    <span className="font-display font-semibold text-[13px]">{mod.label}</span>
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">{mod.group}</span>
                  </td>
                  {roles.map((role) => {
                    const keys = mod.actions.map((a) => a.key);
                    const onCount = keys.filter((k) => has(role, k)).length;
                    return (
                      <td key={role} className="p-2.5 text-center">
                        <Checkbox
                          checked={onCount === keys.length ? true : onCount > 0 ? 'indeterminate' : false}
                          onCheckedChange={() => toggleModule(role, mod)}
                          data-testid={`module-toggle-${role}-${mod.key}`}
                        />
                      </td>
                    );
                  })}
                </tr>,
                ...mod.actions.map((a) => (
                  <tr key={a.key} className="border-b last:border-0 hover:bg-muted/15 transition-colors">
                    <td className="p-2 pl-8 text-[13px] text-muted-foreground sticky left-0 bg-card z-10">{a.label}</td>
                    {roles.map((role) => (
                      <td key={role} className="p-2 text-center">
                        <Checkbox
                          checked={has(role, a.key)}
                          onCheckedChange={() => toggle(role, a.key)}
                          data-testid={`perm-cell-${role}-${a.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                )),
              ]
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
